import React, { useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Euro,
  Package,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { NumberInput } from './ui/number-input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

export interface CustomField {
  id: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio';
  required: boolean;
  scope: 'team' | 'participant';
  options?: string[];
  order: number;
  // Champs pour options payantes (goodies)
  is_paid?: boolean;
  price_cents?: number;
  max_quantity?: number;
  stock_available?: number;
  stock_sold?: number;
  /** Question « rôle » du formulaire bénévoles (éditée à part, jamais ici). */
  is_role?: boolean;
  /** Libellé proposé → valeur de rôle stockée sur le bénévole. */
  role_map?: Record<string, string>;
  /** Question « disponibilité » du formulaire bénévoles, éditée à part. */
  is_availability?: boolean;
  /** Libellé proposé → créneaux couverts ({date, start, end}), voir volunteers/disponibilite.ts. */
  slot_map?: Record<string, { date: string; start: string; end: string }[]>;
}

interface CustomFieldBuilderProps {
  fields: CustomField[];
  onChange: (fields: CustomField[]) => void;
  /** Affiche le sélecteur de portée équipe/participant (défaut: true). */
  showScope?: boolean;
  /** Affiche la section « option payante » (goodies) (défaut: true). */
  showPaidOptions?: boolean;
}

const CustomFieldBuilder: React.FC<CustomFieldBuilderProps> = ({
  fields,
  onChange,
  showScope = true,
  showPaidOptions = true,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [formData, setFormData] = useState({
    label: '',
    type: 'text' as CustomField['type'],
    required: false,
    scope: 'team' as CustomField['scope'],
    options: [] as string[],
    // Options payantes
    is_paid: false,
    price_cents: 0,
    max_quantity: 1,
    stock_available: undefined as number | undefined,
  });
  const [optionInput, setOptionInput] = useState('');

  const handleOpenDialog = (field?: CustomField) => {
    if (field) {
      setEditingField(field);
      setFormData({
        label: field.label,
        type: field.type,
        required: field.required,
        scope: field.scope,
        options: field.options || [],
        is_paid: field.is_paid || false,
        price_cents: field.price_cents || 0,
        max_quantity: field.max_quantity || 1,
        stock_available: field.stock_available,
      });
    } else {
      setEditingField(null);
      setFormData({
        label: '',
        type: 'text',
        required: false,
        scope: 'team',
        options: [],
        is_paid: false,
        price_cents: 0,
        max_quantity: 1,
        stock_available: undefined,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingField(null);
    setOptionInput('');
  };

  const handleSaveField = () => {
    if (!formData.label.trim()) return;

    const newField: CustomField = {
      id: editingField?.id || uuidv4(),
      label: formData.label,
      type: formData.type,
      required: formData.required,
      scope: formData.scope,
      options: ['select', 'checkbox', 'radio'].includes(formData.type) ? formData.options : undefined,
      order: editingField?.order ?? fields.length,
      // Options payantes
      is_paid: formData.is_paid,
      price_cents: formData.is_paid ? formData.price_cents : undefined,
      max_quantity: formData.is_paid ? formData.max_quantity : undefined,
      stock_available: formData.is_paid ? formData.stock_available : undefined,
      stock_sold: editingField?.stock_sold || 0,
    };

    if (editingField) {
      // Mise à jour
      onChange(fields.map(f => f.id === editingField.id ? newField : f));
    } else {
      // Ajout
      onChange([...fields, newField]);
    }

    handleCloseDialog();
  };

  const handleDeleteField = (id: string) => {
    onChange(fields.filter(f => f.id !== id));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newFields = [...fields];
    [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
    // Mettre à jour l'ordre
    newFields.forEach((f, i) => f.order = i);
    onChange(newFields);
  };

  const handleMoveDown = (index: number) => {
    if (index === fields.length - 1) return;
    const newFields = [...fields];
    [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    // Mettre à jour l'ordre
    newFields.forEach((f, i) => f.order = i);
    onChange(newFields);
  };

  const handleAddOption = () => {
    if (!optionInput.trim()) return;
    setFormData({
      ...formData,
      options: [...formData.options, optionInput.trim()],
    });
    setOptionInput('');
  };

  const handleRemoveOption = (index: number) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index),
    });
  };

  const getTypeLabel = (type: CustomField['type']) => {
    const labels: Record<CustomField['type'], string> = {
      text: 'Texte court',
      textarea: 'Texte long',
      select: 'Liste déroulante',
      checkbox: 'Cases à cocher',
      radio: 'Boutons radio',
    };
    return labels[type];
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Champs personnalisés</h3>
        <Button variant="outline" onClick={() => handleOpenDialog()}>
          <Plus size={18} className="mr-2" />
          Ajouter un champ
        </Button>
      </div>

      {fields.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            Aucun champ personnalisé. Ajoutez-en pour collecter des informations supplémentaires.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {fields.map((field, index) => (
            <Card key={field.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <GripVertical size={20} className="text-muted-foreground cursor-grab" />

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{field.label}</span>
                      {field.required && (
                        <Badge variant="destructive" className="text-xs">
                          Requis
                        </Badge>
                      )}
                      <Badge
                        className={
                          field.scope === 'team'
                            ? 'bg-white/[0.10] text-foreground border-white/30'
                            : 'bg-transparent text-muted-foreground border-white/20'
                        }
                      >
                        {field.scope === 'team' ? 'Équipe' : 'Participant'}
                      </Badge>
                      {field.is_paid && field.price_cents && (
                        <Badge className="bg-white/[0.06] text-foreground border-white/20">
                          <Euro size={12} className="mr-1" />
                          +{(field.price_cents / 100).toFixed(2)}€
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {getTypeLabel(field.type)}
                      {field.options && ` (${field.options.length} options)`}
                      {field.is_paid && field.stock_available !== undefined && (
                        <span className="ml-2">
                          <Package size={12} className="inline mr-1" />
                          Stock: {(field.stock_available || 0) - (field.stock_sold || 0)}/{field.stock_available}
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronUp size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === fields.length - 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronDown size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(field)}
                      className="h-8 w-8 p-0 text-muted-foreground"
                    >
                      <Edit size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteField(field.id)}
                      className="h-8 w-8 p-0 text-destructive"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog de configuration */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {editingField ? 'Modifier le champ' : 'Ajouter un champ'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Libellé - pleine largeur */}
            <div className="space-y-2">
              <Label htmlFor="label">Libellé du champ *</Label>
              <Input
                id="label"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Ex: Taille de t-shirt"
              />
            </div>

            {/* Type + Portée sur la même ligne */}
            <div className={`grid ${showScope ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
              <div className="space-y-2">
                <Label htmlFor="type">Type de champ</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: CustomField['type']) =>
                    setFormData({
                      ...formData,
                      type: value,
                      options: [],
                    })
                  }
                >
                  <SelectTrigger id="type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Texte court</SelectItem>
                    <SelectItem value="textarea">Texte long</SelectItem>
                    <SelectItem value="select">Liste déroulante</SelectItem>
                    <SelectItem value="checkbox">Cases à cocher</SelectItem>
                    <SelectItem value="radio">Boutons radio</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {showScope && (
                <div className="space-y-2">
                  <Label htmlFor="scope">Portée</Label>
                  <Select
                    value={formData.scope}
                    onValueChange={(value: CustomField['scope']) =>
                      setFormData({
                        ...formData,
                        scope: value,
                      })
                    }
                  >
                    <SelectTrigger id="scope">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team">Équipe (1× par inscription)</SelectItem>
                      <SelectItem value="participant">Participant (chacun répond)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="required"
                checked={formData.required}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, required: checked === true })
                }
              />
              <Label htmlFor="required" className="cursor-pointer">
                Champ requis
              </Label>
            </div>

            {/* Section Option Payante */}
            {showPaidOptions && formData.type === 'checkbox' && (
              <div className="border-t pt-4 mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="is_paid" className="font-medium">Option payante</Label>
                    <p className="text-xs text-muted-foreground">
                      Ajouter un prix à cette option (ex: t-shirt, goodies)
                    </p>
                  </div>
                  <Switch
                    id="is_paid"
                    checked={formData.is_paid}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_paid: checked })
                    }
                  />
                </div>

                {formData.is_paid && (
                  <div className="pl-4 border-l-2 border-white/[0.14] space-y-4">
                    {/* Prix + Quantité max + Stock sur la même ligne */}
                    <div className="grid grid-cols-3 gap-4">
                      {/* Prix */}
                      <div className="space-y-2">
                        <Label htmlFor="price">Prix (€) *</Label>
                        <div className="relative">
                          <Euro size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <NumberInput
                            id="price"
                            step="0.01"
                            min="0"
                            className="pl-9"
                            value={(formData.price_cents / 100).toFixed(2)}
                            onChange={(e) => setFormData({
                              ...formData,
                              price_cents: Math.round(parseFloat(e.target.value || '0') * 100)
                            })}
                          />
                        </div>
                      </div>

                      {/* Quantité max */}
                      <div className="space-y-2">
                        <Label htmlFor="max_quantity">Qté max</Label>
                        <NumberInput
                          id="max_quantity"
                          min="1"
                          value={formData.max_quantity}
                          onChange={(e) => setFormData({
                            ...formData,
                            max_quantity: parseInt(e.target.value || '1')
                          })}
                        />
                      </div>

                      {/* Stock */}
                      <div className="space-y-2">
                        <Label htmlFor="stock">Stock</Label>
                        <NumberInput
                          id="stock"
                          min="0"
                          placeholder="∞"
                          value={formData.stock_available ?? ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            stock_available: e.target.value ? parseInt(e.target.value) : undefined
                          })}
                        />
                      </div>
                    </div>

                    {/* Légende sous les champs */}
                    <p className="text-xs text-muted-foreground">
                      Qté max = par inscription • Stock vide = illimité
                      {editingField?.stock_sold && editingField.stock_sold > 0 && (
                        <span className="text-muted-foreground ml-2">
                          ({editingField.stock_sold} déjà vendu{editingField.stock_sold > 1 ? 's' : ''})
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Options pour select, checkbox, radio */}
            {['select', 'checkbox', 'radio'].includes(formData.type) && (
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 bg-muted rounded-md"
                    >
                      <span className="flex-1 text-sm">{option}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveOption(index)}
                        className="h-6 w-6 p-0 text-destructive"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nouvelle option"
                      value={optionInput}
                      onChange={(e) => setOptionInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddOption();
                        }
                      }}
                    />
                    <Button variant="outline" onClick={handleAddOption}>
                      Ajouter
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Annuler
            </Button>
            <Button onClick={handleSaveField} disabled={!formData.label.trim()}>
              {editingField ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomFieldBuilder;
